import { fetchSentencesWithWords } from '@services/sentencesService';
import { STIMULUS_WORDS_COUNT } from '@shared/Resources/Monitor/types';
import {
  buildTimestamp,
  containsReplacementChar,
  downloadTextFile,
  parseCsvFileWithEncodingFallback,
  unparseCsvWithBom,
} from '../../shared/csvUtils';
import { parseLocale, parsePopulation } from '../../shared/csvParsing';
import { formatSentenceWithSyllables } from '../../shared/formatters';

const REQUIRED_HEADERS = ['text'];

// Must match backend GraphQL enum TextStimuliIntonationEnumType
const INTONATION = Object.freeze({
  FACT: 'FACT',
  QUESTION: 'QUESTION',
  FACT_AND_QUESTION: 'FACT_AND_QUESTION',
});

const INTONATION_ALLOWED_MESSAGE =
  'Allowed (case-insensitive): FACT, QUESTION, FACT_AND_QUESTION (also accepts "Fact & Question").';

const SENTENCE_LENGTH_ALLOWED_MESSAGE =
  'Allowed (case-insensitive): SHORT, MEDIUM, LONG. Leave empty for unmarked.';

const normalizeIntonationInput = (raw) =>
  String(raw)
    .trim()
    .toUpperCase()
    .replace(/\./g, '')
    .replace(/\s*&\s*/g, '_AND_')
    .replace(/\s+/g, '_')
    .replace(/-/g, '_')
    .replace(/__+/g, '_')
    .replace(/^_+|_+$/g, '');

const CANONICAL_INTONATION_BY_NORMALIZED = Object.freeze({
  [INTONATION.FACT]: INTONATION.FACT,
  [INTONATION.QUESTION]: INTONATION.QUESTION,
  [INTONATION.FACT_AND_QUESTION]: INTONATION.FACT_AND_QUESTION,

  // Common variants
  FACT_AND_QUESTIONS: INTONATION.FACT_AND_QUESTION,
  FACT_ANDQUESTION: INTONATION.FACT_AND_QUESTION,
});

const CANONICAL_INTONATION_BY_COLLAPSED = Object.freeze({
  FACT: INTONATION.FACT,
  QUESTION: INTONATION.QUESTION,
  FACTANDQUESTION: INTONATION.FACT_AND_QUESTION,
  FACTANDQUESTIONS: INTONATION.FACT_AND_QUESTION,
});

const parseIntonation = (value) => {
  if (value == null || String(value).trim() === '') return INTONATION.FACT;
  const raw = String(value).trim();

  const normalized = normalizeIntonationInput(raw);
  const direct = CANONICAL_INTONATION_BY_NORMALIZED[normalized];
  if (direct) return direct;

  const collapsed = normalized.replace(/_/g, '');
  const byCollapsed = CANONICAL_INTONATION_BY_COLLAPSED[collapsed];
  if (byCollapsed) return byCollapsed;

  throw new Error(`Invalid intonation "${raw}". ${INTONATION_ALLOWED_MESSAGE}`);
};

const parseSentenceLength = (value) => {
  if (value == null || String(value).trim() === '') return null;
  const normalized = String(value).trim().toUpperCase();
  if (normalized === STIMULUS_WORDS_COUNT.SHORT
    || normalized === STIMULUS_WORDS_COUNT.MEDIUM
    || normalized === STIMULUS_WORDS_COUNT.LONG) {
    return normalized;
  }
  throw new Error(`Invalid sentence length "${value}". ${SENTENCE_LENGTH_ALLOWED_MESSAGE}`);
};

const parseRow = (row, rowNumber) => {
  const text = String(row?.text ?? '').trim();
  if (!text) return { error: `Row ${rowNumber}: missing text` };

  try {
    return {
      value: {
        text,
        locale: parseLocale(row?.locale),
        population: parsePopulation(row?.population),
        intonation: parseIntonation(row?.intonation),
        sentenceLength: parseSentenceLength(row?.sentencelength),
      },
    };
  } catch (e) {
    return { error: `Row ${rowNumber}: ${e.message}` };
  }
};

export function parseSentencesCSV(file) {
  return parseCsvFileWithEncodingFallback({
    file,
    requiredHeaders: REQUIRED_HEADERS,
    parseRow,
    shouldRetryWithNextEncoding: (items) => (items || []).some((i) => containsReplacementChar(i?.text)),
  });
}

export async function exportSentencesCSV() {
  const all = await fetchSentencesWithWords();
  if (!all?.length) {
    throw new Error('No sentences to export');
  }

  const getExportText = (sentence) => {
    const words = sentence?.words;
    const syllabified = Array.isArray(words) && words.length
      ? formatSentenceWithSyllables(words, { originalText: sentence?.text })
      : '';

    if (syllabified) return syllabified;
    return sentence?.text || '';
  };

  const rows = all.map((s) => ({
    text: getExportText(s),
    locale: s?.locale ?? '',
    intonation: s?.intonation ?? '',
    population: Array.isArray(s?.population) ? s.population.join(',') : '',
    sentenceLength: s?.sentenceLength ?? '',
    wordCount: typeof s?.wordCount === 'number' ? s.wordCount : '',
    syllableCount: typeof s?.syllableCount === 'number' ? s.syllableCount : '',
  }));

  const csv = unparseCsvWithBom({
    rows,
    columns: ['text', 'locale', 'intonation', 'sentenceLength', 'population', 'wordCount', 'syllableCount'],
  });
  const filename = `novotalk_sentences_${buildTimestamp()}.csv`;
  downloadTextFile({ content: csv, filename, mimeType: 'text/csv;charset=utf-8;' });
  return { filename, count: rows.length };
}
